import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import ProjectWorkflowFoundationApi from "../../../infrastructure/api/projectWorkflowFoundation";
import {
  createUploadedFileObjectUrl,
  openUploadedFile,
} from "../../../infrastructure/utils/uploadedFileAccess";
import { toJalaliFullDate } from "../../middleware/middleware";

const boolLabel = (value) => {
  if (value == null || value === "") return "—";
  return value ? "بله" : "خیر";
};

const pick = (obj, ...keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const isLikelyImageUrl = (url) => {
  const s = String(url || "");
  if (/\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(s)) return true;
  try {
    const parsed = new URL(s, window.location.origin);
    const pathParam = parsed.searchParams.get("path") || "";
    return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(decodeURIComponent(pathParam));
  } catch {
    return false;
  }
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    fontWeight={700}
    color="text.secondary"
    sx={{ mt: 0.5 }}
  >
    {children}
  </Typography>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

const AuthenticatedAttachmentPreview = ({ path, index }) => {
  const [objectUrl, setObjectUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);
  const showImage = isLikelyImageUrl(path);

  useEffect(() => {
    let revoked = false;
    let current = "";
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { objectUrl: url } = await createUploadedFileObjectUrl(path);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        current = url;
        setObjectUrl(url);
      } catch (err) {
        if (!revoked) setError(err?.message || "بارگذاری فایل ناموفق بود.");
      } finally {
        if (!revoked) setLoading(false);
      }
    };
    load();
    return () => {
      revoked = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [path]);

  const handleOpen = async () => {
    setOpening(true);
    try {
      await openUploadedFile(path, "", {
        fileName: String(path).split("/").pop() || `site-visit-${index + 1}`,
      });
    } catch (err) {
      toast.error(err?.message || "باز کردن فایل ناموفق بود.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <Box
      sx={{
        p: 1,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {loading ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <CircularProgress size={22} />
        </Box>
      ) : null}
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {!loading && !error && objectUrl ? (
        showImage ? (
          <Box
            component="img"
            src={objectUrl}
            alt={`فرم بازدید ${index + 1}`}
            sx={{
              width: "100%",
              maxHeight: 560,
              objectFit: "contain",
              borderRadius: 1,
              bgcolor: "grey.50",
              display: "block",
            }}
          />
        ) : (
          <Box
            component="iframe"
            src={objectUrl}
            title={`فرم بازدید ${index + 1}`}
            sx={{
              width: "100%",
              height: 520,
              border: 0,
              borderRadius: 1,
              bgcolor: "grey.50",
            }}
          />
        )
      ) : null}
      <Box sx={{ mt: 1 }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          disabled={opening || Boolean(error)}
          onClick={handleOpen}
        >
          باز کردن فایل
        </Button>
      </Box>
    </Box>
  );
};

AuthenticatedAttachmentPreview.propTypes = {
  path: PropTypes.string.isRequired,
  index: PropTypes.number,
};

const WorkflowV2SiteVisitDetailsPanel = ({
  requestId,
  source = "SolarentOperator",
  showTrigger = true,
  autoLoad = false,
}) => {
  const [open, setOpen] = useState(Boolean(autoLoad) || !showTrigger);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");

  const loadDetails = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError("");
    try {
      const res = await ProjectWorkflowFoundationApi.getWorkflowV2SiteVisitDetails(
        source,
        requestId
      );
      if (!res.ok) {
        setError(res.message || "خطا در دریافت جزئیات بازدید");
        setDetails(null);
        return;
      }
      setDetails(res.data || null);
      setLoaded(true);
    } catch {
      setError("خطا در دریافت جزئیات بازدید");
      setDetails(null);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [requestId, source]);

  useEffect(() => {
    if ((!showTrigger || autoLoad) && requestId) {
      setOpen(true);
      loadDetails();
    }
  }, [showTrigger, autoLoad, requestId, loadDetails]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) {
      await loadDetails();
    }
  };

  if (!requestId) return null;

  const isAvailable = pick(details, "isAvailable", "IsAvailable");
  const isFormCompleted = Boolean(
    pick(details, "isFormCompleted", "IsFormCompleted")
  );
  const scheduledVisitTime = pick(
    details,
    "scheduledVisitTime",
    "ScheduledVisitTime"
  );
  const statusTitle =
    pick(details, "siteVisitStatusTitleFa", "SiteVisitStatusTitleFa") || "";
  const fileUrls =
    pick(details, "attachedFileUrls", "AttachedFileUrls") ||
    (pick(details, "attachedFileUrl", "AttachedFileUrl")
      ? [pick(details, "attachedFileUrl", "AttachedFileUrl")]
      : []);
  const hasOutcome =
    isFormCompleted ||
    (statusTitle && !String(statusTitle).includes("در حال بررسی"));

  const field = (label, value) => (
    <Grid item xs={12} sm={6} md={4} key={label}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {value == null || value === "" ? "—" : value}
      </Typography>
    </Grid>
  );

  const installationTitle =
    pick(
      details,
      "installationLocationTypeTitleFa",
      "InstallationLocationTypeTitleFa"
    ) || "—";
  const installationOther = pick(
    details,
    "installationLocationOther",
    "InstallationLocationOther"
  );
  const installationDisplay =
    installationOther && String(installationTitle).includes("سایر")
      ? `${installationTitle} (${installationOther})`
      : installationTitle;

  const networkTitle =
    pick(
      details,
      "distributionNetworkTypeTitleFa",
      "DistributionNetworkTypeTitleFa"
    ) || "—";
  const aerialTitle = pick(
    details,
    "aerialNetworkSubtypeTitleFa",
    "AerialNetworkSubtypeTitleFa"
  );
  const networkDisplay =
    aerialTitle && String(networkTitle).includes("هوایی")
      ? `${networkTitle} — ${aerialTitle}`
      : networkTitle;

  return (
    <Box sx={{ mt: showTrigger ? 1 : 0 }}>
      {showTrigger ? (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={handleToggle}
          disabled={loading}
        >
          {open ? "بستن جزئیات بازدید" : "مشاهده جزئیات بازدید"}
        </Button>
      ) : null}

      <Collapse in={open || !showTrigger} unmountOnExit={showTrigger}>
        <Box
          sx={{
            mt: showTrigger ? 1.5 : 0,
            p: showTrigger ? 1.5 : 0,
            borderRadius: 1,
            bgcolor: showTrigger ? "action.hover" : "transparent",
          }}
        >
          {loading ? (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <CircularProgress size={22} />
            </Box>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {loaded && !loading && !error && !isAvailable ? (
            <Alert severity="info">
              هنوز بازدیدی برای این پرونده ثبت نشده یا زمان بازدید هماهنگ نشده است.
            </Alert>
          ) : null}

          {loaded && !loading && !error && isAvailable ? (
            <Stack spacing={2}>
              {!hasOutcome && scheduledVisitTime ? (
                <Alert severity="info">
                  زمان بازدید هماهنگ‌شده:{" "}
                  <strong>{toJalaliFullDate(scheduledVisitTime)}</strong>
                  {" — "}
                  فرم بازدید هنوز توسط پیمانکار/ناظر تکمیل نشده است.
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  color={hasOutcome ? "success" : "primary"}
                  label={
                    statusTitle ||
                    (hasOutcome
                      ? "بازدید تکمیل‌شده"
                      : scheduledVisitTime
                        ? "بازدید برنامه‌ریزی‌شده"
                        : "بازدید")
                  }
                />
                {pick(details, "assignmentStatusTitleFa", "AssignmentStatusTitleFa") ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`ارجاع: ${pick(
                      details,
                      "assignmentStatusTitleFa",
                      "AssignmentStatusTitleFa"
                    )}`}
                  />
                ) : null}
                {pick(details, "requestReferenceId", "RequestReferenceId") ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`پیگیری: ${pick(
                      details,
                      "requestReferenceId",
                      "RequestReferenceId"
                    )}`}
                  />
                ) : null}
              </Stack>

              <SectionTitle>زمان و مسئول بازدید</SectionTitle>
              <Grid container spacing={1.5}>
                {field(
                  "زمان هماهنگ‌شده",
                  scheduledVisitTime ? toJalaliFullDate(scheduledVisitTime) : "—"
                )}
                {field(
                  "زمان انجام بازدید",
                  pick(details, "actualVisitTime", "ActualVisitTime")
                    ? toJalaliFullDate(
                        pick(details, "actualVisitTime", "ActualVisitTime")
                      )
                    : "—"
                )}
                {field(
                  "انجام‌دهنده",
                  [
                    pick(details, "performerRoleTitleFa", "PerformerRoleTitleFa"),
                    pick(details, "performerPartyName", "PerformerPartyName"),
                  ]
                    .filter(Boolean)
                    .join(" — ") || "—"
                )}
                {field(
                  "مسئول بازدید اولیه",
                  pick(
                    details,
                    "initialVisitResponsibleTypeTitleFa",
                    "InitialVisitResponsibleTypeTitleFa"
                  ) || "—"
                )}
                {field(
                  "بازدیدکننده",
                  pick(details, "visitorFullName", "VisitorFullName") || "—"
                )}
                {field(
                  "شرکت مجری",
                  pick(details, "companyName", "CompanyName") || "—"
                )}
              </Grid>

              <Divider />

              <SectionTitle>مشخصات فنی و اشتراک</SectionTitle>
              <Grid container spacing={1.5}>
                {field(
                  "ظرفیت درخواستی",
                  pick(details, "requestedCapacity", "RequestedCapacity") ?? "—"
                )}
                {field(
                  "ظرفیت پیشنهادی",
                  pick(details, "proposedCapacity", "ProposedCapacity") ?? "—"
                )}
                {field("آمپر", pick(details, "amper", "Amper") ?? "—")}
                {field(
                  "ظرفیت ترانس (kVA)",
                  pick(details, "transformerCapacity", "TransformerCapacity") ??
                    "—"
                )}
                {field(
                  "فاصله از ترانس (متر)",
                  pick(
                    details,
                    "distanceFromTransformer",
                    "DistanceFromTransformer"
                  ) ?? "—"
                )}
                {field(
                  "نوع فاز",
                  pick(details, "phaseCountTitleFa", "PhaseCountTitleFa") || "—"
                )}
                {field("شناسه قبض", pick(details, "billId", "BillId") || "—")}
                {field(
                  "شماره اشتراک",
                  pick(details, "subscriptionNumber", "SubscriptionNumber") ||
                    "—"
                )}
                {field(
                  "مساحت قابل نصب (م²)",
                  pick(
                    details,
                    "installableAreaSquareMeter",
                    "InstallableAreaSquareMeter"
                  ) ?? "—"
                )}
                {field("نوع محل نصب", installationDisplay)}
                {field("نوع شبکه", networkDisplay)}
                {field("آدرس محل", pick(details, "address", "Address") || "—")}
              </Grid>

              {hasOutcome ? (
                <>
                  <Divider />
                  <SectionTitle>چک‌لیست فنی</SectionTitle>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`محل نصب مشاع: ${boolLabel(
                        pick(details, "isSharedRooftop", "IsSharedRooftop")
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`محل کنتور: ${boolLabel(
                        pick(details, "hasMeterLocation", "HasMeterLocation")
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`ساختمان زیر ۱۰ سال: ${boolLabel(
                        pick(
                          details,
                          "isBuildingUnder10Yrs",
                          "IsBuildingUnder10Yrs"
                        )
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`آفتاب‌گیر کامل: ${boolLabel(
                        pick(details, "isFullSunlight", "IsFullSunlight")
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`تطابق اشتراک: ${boolLabel(
                        pick(
                          details,
                          "isSubscriptionVerified",
                          "IsSubscriptionVerified"
                        )
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`کنتور هوشمند: ${boolLabel(
                        pick(details, "isSmartMeter", "IsSmartMeter")
                      )}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`شناسه قبض به نام متقاضی: ${boolLabel(
                        pick(
                          details,
                          "isBillIdInApplicantName",
                          "IsBillIdInApplicantName"
                        )
                      )}`}
                    />
                    <Chip
                      size="small"
                      color={
                        pick(
                          details,
                          "isVisitedByDistributionCompanySupervisor",
                          "IsVisitedByDistributionCompanySupervisor"
                        )
                          ? "warning"
                          : "default"
                      }
                      variant="outlined"
                      label={`ناظر شرکت توزیع: ${boolLabel(
                        pick(
                          details,
                          "isVisitedByDistributionCompanySupervisor",
                          "IsVisitedByDistributionCompanySupervisor"
                        )
                      )}`}
                    />
                  </Stack>
                </>
              ) : null}

              {pick(details, "description", "Description") ? (
                <>
                  <Divider />
                  <SectionTitle>توضیحات ناظر</SectionTitle>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {pick(details, "description", "Description")}
                  </Typography>
                </>
              ) : null}

              <Divider />
              <SectionTitle>فایل / عکس فرم بازدید</SectionTitle>
              {fileUrls.length > 0 ? (
                <Stack spacing={1.5}>
                  {fileUrls.map((url, index) => (
                    <AuthenticatedAttachmentPreview
                      key={`${url}-${index}`}
                      path={url}
                      index={index}
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  هنوز فایل پیوست فرم بازدید آپلود نشده است.
                </Typography>
              )}
            </Stack>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
};

WorkflowV2SiteVisitDetailsPanel.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  source: PropTypes.string,
  showTrigger: PropTypes.bool,
  autoLoad: PropTypes.bool,
};

export default WorkflowV2SiteVisitDetailsPanel;
